import { useState, useEffect } from 'react'
import { Drawer, Checkbox, Button, Space, Divider, Typography, Tag, theme } from 'antd'
import { HolderOutlined } from '@ant-design/icons'
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuth } from '@/contexts/AuthContext'
import { type DashboardConfig, WIDGET_CATALOG, LINK_CATALOG, ROLE_DEFAULTS, checkPermission } from '@/types/dashboard'

interface Props {
  open: boolean
  onClose: () => void
  config: DashboardConfig
  onSave: (config: DashboardConfig) => void
}

const { Text, Title } = Typography

const CATEGORY_LABEL: Record<string, string> = {
  kpi: 'Indicatori (KPI)',
  table: 'Tabelle',
  calendar: 'Calendario',
}

// ── Singolo item sortable ────────────────────────────────────────────────────

function SortableWidgetItem({ id, label }: { id: string; label: string }) {
  const { token } = theme.useToken()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        zIndex: isDragging ? 999 : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '7px 10px',
          background: isDragging ? token.colorInfoBg : token.colorFillAlter,
          borderRadius: 6,
          border: `1px solid ${isDragging ? token.colorInfoBorder : token.colorBorderSecondary}`,
          marginBottom: 4,
          boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : undefined,
          userSelect: 'none',
          cursor: 'default',
        }}
        {...attributes}
      >
        {/* Handle — solo questa area avvia il drag */}
        <span
          {...listeners}
          style={{
            cursor: 'grab',
            color: token.colorTextQuaternary,
            marginRight: 10,
            fontSize: 16,
            lineHeight: 1,
            touchAction: 'none',
            flexShrink: 0,
          }}
          title="Trascina per riordinare"
        >
          <HolderOutlined />
        </span>
        <Text style={{ flex: 1, fontSize: 13 }}>{label}</Text>
      </div>
    </div>
  )
}

// ── Lista sortable dei widget attivi ─────────────────────────────────────────

function SortableWidgetList({
  draft,
  setDraft,
  labelMap,
}: {
  draft: DashboardConfig
  setDraft: React.Dispatch<React.SetStateAction<DashboardConfig>>
  labelMap: Record<string, string>
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setDraft((prev) => {
        const oldIndex = prev.widgets.indexOf(String(active.id))
        const newIndex = prev.widgets.indexOf(String(over.id))
        return { ...prev, widgets: arrayMove(prev.widgets, oldIndex, newIndex) }
      })
    }
  }

  if (draft.widgets.length === 0) {
    return <Text type="secondary" style={{ fontSize: 12 }}>Nessun widget selezionato</Text>
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={draft.widgets} strategy={verticalListSortingStrategy}>
        {draft.widgets.map((id) => (
          <SortableWidgetItem key={id} id={id} label={labelMap[id] ?? id} />
        ))}
      </SortableContext>
    </DndContext>
  )
}

// ── Drawer principale ────────────────────────────────────────────────────────

export function DashboardConfigDrawer({ open, onClose, config, onSave }: Props) {
  const [draft, setDraft] = useState<DashboardConfig>(config)
  const { hasPermission } = usePermissions()
  const { user } = useAuth()

  useEffect(() => {
    if (open) setDraft(config)
  }, [open, config])

  const availableWidgets = WIDGET_CATALOG.filter(w => checkPermission(w.permission, hasPermission))
  const availableLinks = LINK_CATALOG.filter(l => checkPermission(l.permission, hasPermission))

  const labelMap = Object.fromEntries(WIDGET_CATALOG.map((w) => [w.id, w.label]))

  const toggleWidget = (id: string) => {
    setDraft((prev) => ({
      ...prev,
      widgets: prev.widgets.includes(id)
        ? prev.widgets.filter((w) => w !== id)
        : [...prev.widgets, id],
    }))
  }

  const toggleLink = (id: string) => {
    setDraft((prev) => ({
      ...prev,
      links: prev.links.includes(id)
        ? prev.links.filter((l) => l !== id)
        : [...prev.links, id],
    }))
  }

  const handleReset = () => {
    const ruolo = user?.ruolo ?? ''
    const def = ROLE_DEFAULTS[ruolo] ?? ROLE_DEFAULTS['ADMIN'] ?? { widgets: [], links: [] }
    setDraft(def)
  }

  const categories = ['kpi', 'table', 'calendar'] as const

  return (
    <Drawer
      title="Personalizza Dashboard"
      open={open}
      onClose={onClose}
      width={340}
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button size="small" onClick={handleReset}>Ripristina default</Button>
          <Space>
            <Button size="small" onClick={onClose}>Annulla</Button>
            <Button type="primary" size="small" onClick={() => onSave(draft)}>Salva</Button>
          </Space>
        </Space>
      }
    >
      {/* ── Sezione ordine widget attivi ── */}
      <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
        Ordine widget attivi
      </Title>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Trascina <HolderOutlined /> per riposizionare
      </Text>
      <Divider style={{ margin: '6px 0 10px' }} />
      <SortableWidgetList draft={draft} setDraft={setDraft} labelMap={labelMap} />

      {/* ── Sezione selezione widget ── */}
      <Title level={5} style={{ marginTop: 24, marginBottom: 4 }}>
        Widget disponibili
      </Title>
      {categories.map((cat) => {
        const catWidgets = availableWidgets.filter((w) => w.category === cat)
        if (catWidgets.length === 0) return null
        return (
          <div key={cat} style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
              {CATEGORY_LABEL[cat]}
            </Text>
            <Divider style={{ margin: '4px 0 8px' }} />
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {catWidgets.map((w) => (
                <Checkbox
                  key={w.id}
                  checked={draft.widgets.includes(w.id)}
                  onChange={() => toggleWidget(w.id)}
                >
                  {w.label}
                  {draft.widgets.includes(w.id) && (
                    <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>
                      #{draft.widgets.indexOf(w.id) + 1}
                    </Tag>
                  )}
                </Checkbox>
              ))}
            </Space>
          </div>
        )
      })}

      {/* ── Sezione link rapidi ── */}
      <Title level={5} style={{ marginTop: 20 }}>Link rapidi</Title>
      <Divider style={{ margin: '4px 0 8px' }} />
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        {availableLinks.map((l) => (
          <Checkbox
            key={l.id}
            checked={draft.links.includes(l.id)}
            onChange={() => toggleLink(l.id)}
          >
            {l.label}
          </Checkbox>
        ))}
      </Space>
    </Drawer>
  )
}
